import axios from "axios";
import AuthService from "@/services/Auth.service";
import { EventBus } from "@/eventbus.js";
import { auth0 } from "@/auth/_globals.js";
import { useImpersonation } from "@/composables/useImpersonation";
import { useToast } from "vue-toastification";

const { impersonatedUser } = useImpersonation();
const toast = useToast();

let pendingRequestsMap = new Map(); // map of pending requests that shows global loading spinner
function startedRequest(name) {
	pendingRequestsMap.set(name, (pendingRequestsMap.get(name) || 0) + 1);
	EventBus.$emit("xhr", true);
}

function finishedRequest(name) {
	// no need to -1 numRequests if we did not have a +1!!
	if (pendingRequestsMap.has(name)) {
		const nbPending = pendingRequestsMap.get(name);
		if (nbPending <= 1) pendingRequestsMap.delete(name);
		else {
			pendingRequestsMap.set(
				name,
				(pendingRequestsMap.get(name) || 0) - 1
			);
		}
	}
	if (pendingRequestsMap.size === 0) {
		EventBus.$emit("xhr", false);
	}
}

// to increment/decrement counter
EventBus.$on("addXhr", startedRequest);
EventBus.$on("removeXhr", finishedRequest);

function getBaseUrl(url = "") {
	return url.split("?")[0];
}
export default function axiosHelper() {
	const API = import.meta.env.VITE_API;
	axios.interceptors.request.use(async (config) => {
		if (AuthService.isAuthenticated() && config.url?.startsWith(API)) {
			const token = await auth0.getAccessTokenSilently();
			if (impersonatedUser.value) {
				config.headers["X-Impersonate"] = impersonatedUser.value.email;
			}

			config.headers = {
				...config.headers,
				Authorization: `Bearer ${token}`,
				"X-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
				"Accept-Language":
					AuthService.user()?.language || window.navigator.language
			};
		}
		config.headers["Accept-Language"] =
			AuthService.user()?.language || window.navigator.language;

		// axios v1.x uses as default a seializer that will output the array indexes as indexes[]=0&indexes[]=1, while BE doesn't accept well this format. By passing a null serializer, we can use the indexes as indexes=0&indexes=1
		config.paramsSerializer = {
			indexes: null
		};

		// remove the showSpinner parameter that is used only on the console
		if (
			config.params &&
			Object.keys(config.params).includes("showSpinner")
		) {
			delete config.params.showSpinner;
		} else {
			startedRequest(getBaseUrl(config.url));
		}

		return config;
	});

	axios.interceptors.response.use(
		(response) => {
			const config = response.config;
			finishedRequest(getBaseUrl(config.url));

			if (
				["post", "put", "delete"].includes(config.method ?? "") &&
				(config.url ?? "").includes("/configs/")
			) {
				EventBus.$emit("changedConfiguration");
			}

			// Response data
			return response;
		},
		(error) => {
			// responseURL has the query parameters with it, so we need to remove it
			finishedRequest(getBaseUrl(error.request.responseURL));

			// in the future we shoudl use namespace & propose to end session or smth
			if (impersonatedUser.value && error?.response?.status === 403) {
				if (error?.response?.data?.message) {
					toast.error(error?.response?.data?.message);
				}
			}

			return Promise.reject(error);
		}
	);
}
